import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useEvents } from "api/events";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { CUSTOM_EVENTS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { AttributesDetails } from "../SingleEvent/AttributesDetails/AttributesDetails";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    eventNameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(5),
    },
    tooltipContent: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(5),
      wordBreak: "break-word",
    },
    tooltipTitle: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "24px",
      color: theme.gray800,
    },
    tooltipDescription: {
      whiteSpace: "break-spaces",
      marginTop: theme.spacing(2),
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 400,
    },
  }),
);

export function CustomEventsList() {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(CUSTOM_EVENTS_LIST_ID);
  const appDispatch = useAppDispatch();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[CUSTOM_EVENTS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      CUSTOM_EVENTS_LIST_ID,
    );

    setFilters(filters);
  };

  const { useListOfCustomEvents } = useEvents();
  const { data: listOfCustomEvents, error } = useListOfCustomEvents({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "eventDate",
    dynamicSearch,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "eventDate" }),
      property: "eventDate",
      isSortable: true,
      hasMinWidthThreshold: true,
    },
    {
      headerText: message({ id: "eventName" }),
      property: "eventName",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "eventID" }),
      property: "eventId",
      isSortable: false,
      isVisible: false,
    },
    {
      headerText: message({ id: "emailAddress" }),
      property: "customerData:email",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "customerData:phone",
      isSortable: true,
      align: "right",
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "customerData:loyaltyCardNumber",
      isSortable: true,
      align: "right",
    },
    {
      headerText: message({ id: "status" }),
      property: "customerId",
      isSortable: false,
      tooltip: (
        <>
          <Typography className={classes.tooltipTitle}>
            {message({ id: "status" })}
          </Typography>
          <Typography className={classes.tooltipDescription}>
            {message({ id: "statusEventInfo" })}
          </Typography>
        </>
      ),
    },
  ]);

  const itemsList = useMemo(
    () =>
      listOfCustomEvents?.items?.map((item) => ({
        cells: [
          { content: OLDate(item.eventDate), property: "eventDate" },
          {
            content: (
              <div className={classes.eventNameRow}>
                {item.eventName}
                {Object.keys(item.body)?.length ? (
                  <span className={classes.eventNameRow}>
                    <AttributesDetails attributes={item.body} showCount={3} />
                  </span>
                ) : null}
              </div>
            ),
            customOverflowTooltip: (
              <div className={classes.tooltipContent}>
                {item.eventName}
                {Object.keys(item.body)?.length ? (
                  <AttributesDetails attributes={item.body} showCount={3} />
                ) : null}
              </div>
            ),
            nonClickable: true,
            property: "eventName",
          },
          {
            content: item.eventId,
            property: "eventId",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.email,
            property: "customerData:email",
            dataHjSuppress: true,
          },
          {
            content: item.customerData?.phone,
            property: "customerData:phone",
            dataHjSuppress: true,
            align: "right",
          },
          {
            content: item.customerData?.loyaltyCardNumber,
            property: "customerData:loyaltyCardNumber",
            dataHjSuppress: true,
            align: "right",
          },
          {
            content: message({
              id: item.customerId ? "matched" : "notMatched",
            }),
            property: "customerId",
          },
        ],
        id: item.customEventId,
        metadata: {
          customerId: item.customerId,
        },
      })),
    [
      listOfCustomEvents?.items,
      classes.eventNameRow,
      classes.tooltipContent,
      message,
    ],
  );

  const items = listOfCustomEvents?.items ? (itemsList as Row[]) : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && listOfCustomEvents) {
      setPersistentFilters(filters, CUSTOM_EVENTS_LIST_ID);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, listOfCustomEvents]);

  return (
    <>
      <TopHeader title={message({ id: "customEvents" })} />
      <Subheader>
        <FiltersV2
          data-test-id="custom-events"
          donutLabel={message({ id: "events" })}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={listOfCustomEvents?.total}
          properties={[
            {
              query: "customerData:email",
              label: message({ id: "email" }),
              type: "text",
            },
            {
              query: "eventDate",
              label: message({ id: "customEventDate" }),
              type: "datetime",
            },
            {
              query: "eventName",
              label: message({ id: "customEventName" }),
              type: "text",
            },
            {
              query: "customerData:loyaltyCardNumber",
              label: message({ id: "loyaltyCardNumber" }),
              type: "text",
            },
            {
              query: "customerId",
              label: message({ id: "memberID" }),
              type: "text",
            },
            {
              query: "eventId",
              label: message({ id: "eventID" }),
              type: "text",
            },
            {
              query: "customerData:phone",
              label: message({ id: "phoneNumber" }),
              type: "text",
            },
            {
              query: "type",
              label: message({ id: "type" }),
              type: "text",
            },
          ]}
        />
        <SubheaderLinkButton
          text={message({ id: "customEventSchemas" })}
          link={links.customEventsSchemaList}
          data-test-id="custom-events-custom-event-schemas-button"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="eventName"
            properties={[
              {
                id: "eventName",
                label: "eventName",
              },
              { id: "customerData:email", label: "email" },
              { id: "customerData:phone", label: "phone" },
            ]}
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: CUSTOM_EVENTS_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={CUSTOM_EVENTS_LIST_ID}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.customEvent(`${id}`)),
              label: message({ id: "seeDetails" }),
            },
            {
              action: (_, item?: Row) => {
                navigate(
                  links.singleMemberCustomEvents(item?.metadata?.customerId),
                );
              },
              label: message({ id: "seeMemberProfile" }),
              hideCondition: (item: Row) => !item?.metadata?.customerId,
            },
          ],
        }}
        columns={listOfCustomEvents && headItems}
        data-test-id="custom-events"
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        onRowClick={(id: string) => {
          navigate(links.customEvent(id as string));
        }}
        pagination={{
          count: calculateCount(
            listOfCustomEvents?.total.filtered,
            listOfCustomEvents?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={listOfCustomEvents && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "eventDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        clearFilters={() => setFilters([])}
      />
    </>
  );
}
